import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSyncAll, useSyncPreview } from "../api/queries";
import type { SyncPreviewCourse } from "../api/types";
import { decodeEntities } from "../lib/format";
import { BrandButton } from "./courses/ui";
import { Button, ErrorBanner, StatusBadge, Td, Th } from "./ui";

type SyncMutation = ReturnType<typeof useSyncAll>;

// "Sync" button that previews first: the user sees what would change per
// course and syncs only the ones they tick (new + changed by default).
export function SyncButton({ sync, disabled, onStarted, children = "Sync from Moodle", variant = "primary" }: {
  sync: SyncMutation;
  disabled?: boolean;
  onStarted?: () => void;
  children?: ReactNode;
  variant?: "primary" | "secondary" | "brand";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} loading={sync.isPending} disabled={disabled} onClick={() => setOpen(true)}>{children}</Button>
      {open && (
        <SyncDialog
          onClose={() => setOpen(false)}
          onSync={(ids) => {
            sync.mutate(ids);
            setOpen(false);
            onStarted?.();
          }}
        />
      )}
    </>
  );
}

const DEFAULT_SELECTED = new Set(["new", "changed"]);

export function SyncDialog({ onClose, onSync }: { onClose: () => void; onSync: (courseMoodleIds?: number[]) => void }) {
  const preview = useSyncPreview();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  // The preview is as slow as a real sync: run it once per open, even under
  // StrictMode's double effect.
  const started = useRef(false);
  const { mutate } = preview;
  const run = useCallback(
    () => mutate(undefined, {
      onSuccess: (res) => setSelected(new Set(res.courses.filter((c) => DEFAULT_SELECTED.has(c.status)).map((c) => c.moodle_id))),
    }),
    [mutate]
  );
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  const data = preview.data;
  const rows = useMemo(() => {
    const order = { new: 0, changed: 1, removed: 2, unchanged: 3 } as Record<string, number>;
    return (data?.courses ?? [])
      .filter((c) => showUnchanged || c.status !== "unchanged")
      .sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9) || a.fullname.localeCompare(b.fullname));
  }, [data, showUnchanged]);

  // Removed courses can't be synced selectively: only a full sync retires them.
  const selectable = rows.filter((c) => c.status !== "removed");
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.moodle_id));
  const removed = data?.totals.removed ?? 0;

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <DialogShell
      wide={!!data}
      title="Sync From the Moodle"
      onClose={onClose}
      footer={
        <>
          <BrandButton className="w-[156px] !min-h-[54px]" onClick={onClose}>Cancel</BrandButton>
          <BrandButton
            variant="soft"
            className="!min-h-[54px] min-w-[156px] font-semibold"
            disabled={!data}
            onClick={() => onSync(undefined)}
            title="Sync every course. Also retires courses that were removed from Moodle."
          >
            Full Sync{removed ? ` (retires ${removed} removed)` : ""}
          </BrandButton>
          <BrandButton
            variant="primary"
            className="!min-h-[54px] min-w-[156px] font-semibold"
            disabled={!data || !selected.size}
            onClick={() => onSync([...selected])}
          >
            Sync Selected ({selected.size})
          </BrandButton>
        </>
      }
    >
      <ErrorBanner error={preview.error} />
      {preview.isPending && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-[#FAFAFA] px-4 py-10 text-[15px] font-medium leading-8 text-gray-900">
          <span className="h-7 w-7 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" aria-hidden />
          <span className="flex-1 text-center">
            Checking Moodle for changes. This makes the same calls as a real sync, so it can take a while. Nothing is saved.
          </span>
        </div>
      )}
      {preview.isError && <BrandButton size="sm" onClick={run}>Try again</BrandButton>}
      {data && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge status="new" label={`${data.totals.new} new`} />
            <StatusBadge status="changed" label={`${data.totals.changed} changed`} />
            <StatusBadge status="unchanged" label={`${data.totals.unchanged} unchanged`} />
            {removed > 0 && <StatusBadge status="removed" label={`${removed} removed`} />}
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />
              Show unchanged courses
            </label>
          </div>
          {data.missing.length > 0 && (
            <p className="mb-3 text-xs text-amber-800">Not found in Moodle: {data.missing.join(", ")}</p>
          )}
          {!rows.length ? (
            <p className="py-6 text-center text-sm text-gray-500">Everything is up to date. Nothing to sync.</p>
          ) : (
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(selectable.map((c) => c.moodle_id)))}
                    />
                  </Th>
                  <Th>Course</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Created</Th>
                  <Th className="text-right">Updated</Th>
                  <Th className="text-right">Deleted</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((c) => (
                  <Fragment key={c.moodle_id}>
                    <tr className={c.status === "unchanged" ? "text-gray-500" : ""}>
                      <Td className="w-8">
                        <input
                          type="checkbox"
                          aria-label={`Select ${c.fullname}`}
                          disabled={c.status === "removed"}
                          checked={selected.has(c.moodle_id)}
                          onChange={() => toggle(c.moodle_id)}
                        />
                      </Td>
                      <Td>
                        <div className="font-medium">{c.fullname}</div>
                        <div className="text-xs text-gray-500">{c.shortname} · id {c.moodle_id}</div>
                      </Td>
                      <Td><StatusBadge status={c.status} /></Td>
                      <Td className="text-right tabular-nums">{c.created}</Td>
                      <Td className="text-right tabular-nums">{c.updated}</Td>
                      <Td className="text-right tabular-nums">{c.deleted}</Td>
                      <Td className="text-right">
                        {c.changes.length > 0 && (
                          <button className="text-xs text-blue-700 hover:underline" onClick={() => setExpanded(expanded === c.moodle_id ? null : c.moodle_id)}>
                            {expanded === c.moodle_id ? "Hide" : "Details"}
                          </button>
                        )}
                      </Td>
                    </tr>
                    {expanded === c.moodle_id && (
                      <tr>
                        <Td colSpan={7} className="bg-gray-50"><PreviewChanges course={c} /></Td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {removed > 0 && (
            <p className="mt-3 text-xs text-gray-500">
              Removed courses can't be picked: syncing selected courses never retires others. Use Full sync to retire them.
            </p>
          )}
        </>
      )}
    </DialogShell>
  );
}

function PreviewChanges({ course }: { course: SyncPreviewCourse }) {
  return (
    <div className="text-xs">
      <div className="mb-2 flex flex-wrap gap-3 text-gray-600">
        {Object.entries(course.by_entity).map(([entity, n]) => (
          <span key={entity}>{entity}: +{n.created} ~{n.updated} −{n.deleted}</span>
        ))}
      </div>
      <ul className="max-h-60 space-y-0.5 overflow-y-auto">
        {course.changes.map((ch, i) => {
          const p = ch.payload ?? {};
          const name = p.fullname ?? p.name ?? p.filename ?? p.shortname;
          return (
            <li key={i} className="flex gap-2">
              <StatusBadge status={ch.action} />
              <span className="text-gray-500">{ch.entity_type}</span>
              <span>{typeof name === "string" ? decodeEntities(name) : `#${ch.entity_moodle_id}`}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Modal frame from the admin design: brand-coloured title, boxed close
// button, centred actions. Kept local so the shared Modal is unchanged.
function DialogShell({ title, onClose, children, footer, wide }: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/25 p-4 sm:p-10" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={`flex max-h-[calc(100vh-5rem)] w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl ${wide ? "max-w-4xl" : "max-w-[680px]"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 px-6 py-6 shadow-[0_1px_0_rgba(16,24,40,0.06)]">
          <h2 className="text-[22px] font-medium text-[#0B3B57]">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-gray-50 text-[#0B5785] hover:bg-gray-100"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-6 sm:px-14">{children}</div>
        <footer className="flex flex-wrap items-center justify-center gap-6 px-6 pb-8 pt-6">{footer}</footer>
      </div>
    </div>
  );
}
